import React from 'react'
import { CloseIcon } from 'src/assets/icons/customIcons/Close'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useFormData } from 'src/views/components/forms/formik/Form'

export function TabFormErrors(): React.ReactElement | null {
  const t = useTranslatable()
  const [formData] = useFormData()
  const [isDismissed, setIsDismissed] = React.useState(false)
  const prevSubmittedRef = React.useRef(formData.submitted)

  // Group field errors by category for better display
  const groupedFieldErrors = React.useMemo(() => {
    const groups = {
      section: [] as { field: string; errors: readonly string[] }[],
      competencies: [] as { field: string; errors: readonly string[] }[],
      semesters: [] as { field: string; errors: readonly string[] }[],
      mentors: [] as { field: string; errors: readonly string[] }[],
      other: [] as { field: string; errors: readonly string[] }[],
    }

    Object.entries(formData.errors.fields).forEach(([field, errors]) => {
      if (field.startsWith('section.')) {
        groups.section.push({ field: field.replace('section.', ''), errors })
      } else if (field.startsWith('competencies')) {
        groups.competencies.push({ field, errors })
      } else if (field.startsWith('semesters')) {
        groups.semesters.push({ field, errors })
      } else if (field.startsWith('mentors')) {
        groups.mentors.push({ field, errors })
      } else {
        groups.other.push({ field, errors })
      }
    })

    return groups
  }, [formData.errors.fields])

  const categoryLabels: Record<string, string> = {
    section: t('portfolio:general'),
    competencies: t('portfolio:competencies'),
    semesters: t('portfolio:semesters_and_mentors'),
    mentors: t('portfolio:mentor'),
    other: t(['სხვა', 'Other']),
  }

  const hasGeneralErrors = formData.errors.general.length > 0
  const hasFieldErrors = Object.keys(formData.errors.fields).length > 0
  const totalErrorCount = formData.errors.general.length + Object.keys(formData.errors.fields).length

  // Reset dismissed state when form is submitted again (submitted changes from false to true)
  React.useEffect(() => {
    // Detect when submitted transitions to true (new submission completed)
    if (formData.submitted && !prevSubmittedRef.current) {
      setIsDismissed(false)
    }
    prevSubmittedRef.current = formData.submitted
  }, [formData.submitted])

  if (!hasGeneralErrors && !hasFieldErrors) return null
  if (isDismissed) return null

  return (
    <div className="my-4 w-full rounded-lg border border-primaryRed/30 bg-primaryRed/10 p-4" role="alert">
      {/* Header with close button */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex size-6 items-center justify-center rounded-full bg-primaryRed">
            <span className="text-xs font-bold text-white">{totalErrorCount}</span>
          </div>
          <span className="font-bold text-primaryRed">{t('error:an_error_occurred')}</span>
        </div>
        <button
          type="button"
          onClick={() => setIsDismissed(true)}
          className="flex size-8 items-center justify-center rounded-full text-primaryRed transition-colors hover:bg-primaryRed/20"
          aria-label={t('common:close')}
        >
          <CloseIcon width="14" />
        </button>
      </div>

      {/* General Errors */}
      {hasGeneralErrors && (
        <ul className="mb-3 list-inside list-disc space-y-1 text-sm text-primaryRed">
          {formData.errors.general.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      )}

      {/* Field Errors by Category */}
      {hasFieldErrors && (
        <div className="space-y-3">
          {Object.entries(groupedFieldErrors).map(([category, fields]) => {
            if (fields.length === 0) return null
            return (
              <div key={category} className="rounded-md bg-white/50 p-3 dark:bg-black/20">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-primaryRed/80">
                  {categoryLabels[category] ?? category}
                </div>
                <ul className="space-y-1 text-sm text-primaryTextColor">
                  {fields.map(({ field, errors }, idx) => (
                    <li key={idx} className="flex flex-col gap-0.5">
                      <span className="font-medium text-captionColor">{field}:</span>
                      {errors.map((error, errorIdx) => (
                        <span key={errorIdx} className="ml-2 text-primaryRed">
                          • {error}
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
